import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiRefreshCw,
    FiXCircle,
    FiExternalLink,
    FiEdit2,
    FiLayers,
    FiHome,
} from "react-icons/fi";
import { SiXero } from "react-icons/si";
import { Grid2X2, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import EditAssignment from "../assignments/EditAssignment";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../config/supabase";
import { getInvoiceStatusText, getInvoiceStatusSource } from "../../lib/common/invoiceDisplay";

const REQUIRED_PERMISSION = "GET_WEEKLY_INV_RECORDS";
const DEFAULT_TIME_ZONE = "Australia/Melbourne";
const FORCE_WEEKLY_INVOICE_POPUP_TEST = false; // change to false after testing

const QUOTE_ORDER = ["roof", "wall", "floor", "timber"];

const QUOTE_LABELS = {
    roof: "Roof",
    wall: "Wall",
    floor: "Floor",
    timber: "Timber",
};

const pad = (value) => String(value).padStart(2, "0");

const getCompanyTimeParts = (timeZone = DEFAULT_TIME_ZONE, date = new Date()) => {
    const parts = new Intl.DateTimeFormat("en-AU", {
        timeZone,
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    }).formatToParts(date);

    const get = (type) => parts.find((part) => part.type === type)?.value;

    return {
        weekday: get("weekday"),
        year: Number(get("year")),
        month: Number(get("month")),
        day: Number(get("day")),
        hour: Number(get("hour")),
        minute: Number(get("minute")),
    };
};

const getCompanyWeekRange = (timeZone = DEFAULT_TIME_ZONE) => {
    const parts = getCompanyTimeParts(timeZone);
    const localDate = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    const day = localDate.getUTCDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;

    const monday = new Date(localDate);
    monday.setUTCDate(localDate.getUTCDate() + diffToMonday);

    const nextMonday = new Date(monday);
    nextMonday.setUTCDate(monday.getUTCDate() + 7);

    return {
        weekStart: `${monday.getUTCFullYear()}-${pad(monday.getUTCMonth() + 1)}-${pad(monday.getUTCDate())}`,
        weekEnd: `${nextMonday.getUTCFullYear()}-${pad(nextMonday.getUTCMonth() + 1)}-${pad(nextMonday.getUTCDate())}`,
    };
};

const shouldShowAfterFridayThree = (timeZone = DEFAULT_TIME_ZONE) => {
    const parts = getCompanyTimeParts(timeZone);

    const dayOrder = {
        Sun: 0,
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6,
    };

    const day = dayOrder[parts.weekday];

    if (day > 5) return true;
    if (day < 5) return false;

    return parts.hour >= 15;
};

const hasPermission = (userProfile) => {
    if (!userProfile) return false;
    if (userProfile.role === "app_owner") return true;
    return userProfile.permissions?.includes(REQUIRED_PERMISSION);
};

const shouldIncludeInvoiceRecord = (quote, company) => {
    const invoiceOptions = company?.settings?.friday_invoice_options || {};

    const notifyStatuses = invoiceOptions?.notify || [null, "not_invoiced"];
    const xeroStatuses = invoiceOptions?.xero || [];

    const notifyStatus = quote?.invoice_state ?? null;
    const xeroStatus = quote?.xero_invoice_status ?? null;

    const mode = company?.settings?.invoice_display_mode || "notify";

    if (mode === "xero") {
        return xeroStatuses.includes(xeroStatus);
    }

    if (mode === "hybrid") {
        if (xeroStatus) return xeroStatuses.includes(xeroStatus);
        return notifyStatuses.includes(notifyStatus);
    }

    return notifyStatuses.includes(notifyStatus);
};

const formatDate = (value) => {
    if (!value) return "No date";
    return new Date(value).toLocaleDateString("en-AU", {
        weekday: "short",
        day: "2-digit",
        month: "short",
    });
};

const InvoiceSourceIcon = memo(({ source }) => {
    if (source === "xero") {
        return <SiXero className="text-blue-500 shrink-0" size={14} />;
    }

    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 264 270"
            fill="none"
            className="shrink-0"
        >
            <path
                d="M96.99 239.52H166.73C164.29 256.59 149.6 269.72 131.87 269.72C114.14 269.72 99.42 256.59 96.98 239.52H96.99ZM154.05 73.61C175.96 73.64 186.41 88.33 186.41 88.33C201.7 88.14 208.86 96.43 208.86 96.43C208.86 96.43 181.01 102.96 178.07 126.19C175.35 147.67 184.64 200.72 114.92 227.26H263.72C263.72 227.26 232.83 190.84 225.83 155.55C220.04 126.27 224.32 49.16 162.83 30.16C162.38 13.43 148.7 0 131.86 0C115.02 0 101.47 13.31 100.9 29.94C37.32 48.53 43.7 126.15 37.89 155.55C36.33 163.37 33.61 171.21 30.33 178.75C119.58 136.52 105.23 73.59 154.06 73.62L154.05 73.61ZM138.14 153.06C138.14 153.06 119.38 175.51 92.19 186.17C64.96 196.83 22.79 193.8 22.79 193.8C12.03 213.05 0 227.24 0 227.24H39.75C136.98 227.24 138.14 153.06 138.14 153.06Z"
                fill="url(#weeklyNotifyGradient)"
            />
            <defs>
                <linearGradient id="weeklyNotifyGradient" x1="211.58" y1="80.31" x2="82.79" y2="243.87" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#33EABC" />
                    <stop offset="1" stopColor="#1F8AFE" />
                </linearGradient>
            </defs>
        </svg>
    );
});

const QuoteTypePill = memo(({ type, count, active, disabled, onClick }) => {
    const Icon = type === "all" ? FiLayers : getQuoteTypeIcon(type);

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold transition-all ${active
                ? "border-gray-950 bg-gray-950 text-white"
                : disabled
                    ? "border-emerald-200 bg-emerald-50 text-emerald-300 cursor-not-allowed hidden"
                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50"
                }`}
        >
            <Icon size={13} />
            <span>{type === "all" ? "All" : QUOTE_LABELS[type]}</span>
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${active ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"
                }`}>
                {count}
            </span>
        </button>
    );
});

const InvoiceRow = memo(({ quote, company, onOpenJob, onEditJob }) => {
    const job = quote.jobs;
    const source = getInvoiceStatusSource(quote, company);
    const invoiceText = getInvoiceStatusText(quote, company);
    const TypeIcon = getQuoteTypeIcon(quote.quote_type);

    return (
        <tr className="border-b border-gray-100 hover:bg-gray-50/80">
            <td className="px-3 py-2.5 flex flex-col gap-1 align-top items-start content-center">
                <div className="font-bold text-gray-900 text-xs whitespace-nowrap">
                    {quote.child_unique_id || job?.unique_id || "Unknown"}
                </div>
                <div className="inline-flex items-center gap-1 text-[11px] text-gray-400 whitespace-nowrap">
                    <TypeIcon size={12} />
                    {QUOTE_LABELS[quote.quote_type] || quote.quote_type}
                </div>
            </td>

            <td className="px-3 py-2.5 align-top min-w-[220px] items-center content-center">
                <div className="font-semibold text-gray-900 text-xs truncate max-w-[280px]">
                    {job?.customer_name || "No customer"}
                </div>
                <div className="text-[11px] text-gray-500 truncate max-w-[360px]">
                    {job?.customer_address || "No address"}
                </div>
            </td>

            <td className="px-3 py-2.5 align-top whitespace-nowrap text-xs text-gray-700 items-center content-center">
                {formatDate(quote.expected_delivery_date)}
                {quote.delivery_time_slot ? (
                    <div className="text-[11px] text-gray-400">{quote.delivery_time_slot}</div>
                ) : null}
            </td>

            <td className="px-3 py-2.5 align-top whitespace-nowrap items-center content-center">
                <div className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-bold text-gray-800">
                    <InvoiceSourceIcon source={source} />
                    {invoiceText}
                </div>
            </td>

            <td className="px-3 py-2.5 align-top items-center content-center">
                <div className="flex items-center gap-1.5">
                    {/* <button
                        type="button"
                        onClick={() => onOpenJob(job?.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] font-bold text-gray-700 hover:border-gray-400 hover:bg-gray-50"
                    >
                        <FiExternalLink size={12} />
                        Open
                    </button> */}

                    <button
                        type="button"
                        onClick={() => onEditJob(job?.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-[11px] font-bold text-blue-700 hover:border-blue-300 hover:bg-blue-100"
                    >
                        <FiEdit2 size={12} />
                        Edit
                    </button>
                </div>
            </td>
        </tr>
    );
});

const getQuoteTypeIcon = (type) => {
    if (type === "roof") return FiHome;
    if (type === "wall") return FiLayers;
    if (type === "floor") return Grid2X2;
    if (type === "timber") return Truck;
    return FiLayers;
};

const fetchFreshUserProfile = async (userId) => {
    if (!userId) return null;

    const { data, error } = await supabase
        .from("users")
        .select("id, permissions, role, status, is_deleted")
        .eq("id", userId)
        .maybeSingle();

    if (error) throw error;

    return data;
};

const WeeklyInvoiceRecordsPopup = () => {
    const { userProfile, company, loading } = useAuth();
    const companyTimeZone = company?.timezone || DEFAULT_TIME_ZONE;
    const navigate = useNavigate();

    const [checking, setChecking] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [records, setRecords] = useState([]);
    const [shouldShow, setShouldShow] = useState(false);
    const [error, setError] = useState("");

    const refreshTimerRef = useRef(null);

    const { weekStart, weekEnd } = useMemo(
        () => getCompanyWeekRange(companyTimeZone),
        [companyTimeZone]
    );

    const [activeFilter, setActiveFilter] = useState("all");
    const [visibleGroups, setVisibleGroups] = useState({
        roof: true,
        wall: true,
        floor: true,
        timber: true,
    });
    const [editingJob, setEditingJob] = useState(null);

    const groupedCounts = useMemo(() => {
        return records.reduce((acc, quote) => {
            acc[quote.quote_type] = (acc[quote.quote_type] || 0) + 1;
            return acc;
        }, {});
    }, [records]);

    const sortedRecords = useMemo(() => {
        return [...records].sort((a, b) => {
            const typeDiff =
                QUOTE_ORDER.indexOf(a.quote_type) - QUOTE_ORDER.indexOf(b.quote_type);

            if (typeDiff !== 0) return typeDiff;

            return new Date(a.expected_delivery_date || 0) - new Date(b.expected_delivery_date || 0);
        });
    }, [records]);

    const visibleRecords = useMemo(() => {
        return sortedRecords.filter((quote) => {
            if (activeFilter !== "all" && quote.quote_type !== activeFilter) return false;
            if (activeFilter === "all" && !visibleGroups[quote.quote_type]) return false;
            return true;
        });
    }, [sortedRecords, activeFilter, visibleGroups]);

    const loadPopupState = useCallback(async () => {
        if (loading) return;

        setError("");
        setChecking(true);

        try {
            if (!userProfile?.id || !company?.id) {
                setShouldShow(false);
                return;
            }

            const freshUserProfile = await fetchFreshUserProfile(userProfile.id);

            if (
                !freshUserProfile ||
                freshUserProfile.is_deleted ||
                freshUserProfile.status !== "active" ||
                !hasPermission(freshUserProfile)
            ) {
                setShouldShow(false);
                return;
            }

            if (!FORCE_WEEKLY_INVOICE_POPUP_TEST && !shouldShowAfterFridayThree(companyTimeZone)) {
                setShouldShow(false);
                return;
            }

            const { data: confirmation, error: confirmationError } = await supabase
                .from("weekly_invoice_confirmations")
                .select("id")
                .eq("company_id", company.id)
                .eq("user_id", userProfile.id)
                .eq("week_start", weekStart)
                .maybeSingle();

            if (confirmationError) throw confirmationError;

            if (confirmation?.id) {
                setShouldShow(false);
                return;
            }

            const { data, error: quoteError } = await supabase
                .from("job_quotes")
                .select(`
                    id,
                    job_id,
                    quote_type,
                    child_unique_id,
                    expected_delivery_date,
                    delivery_time_slot,
                    invoice_state,
                    xero_invoice_status,
                    xero_invoice_number,
                    xero_invoice_id,
                    jobs!inner (
                        id,
                        company_id,
                        unique_id,
                        customer_name,
                        customer_address,
                        deleted
                    )
                `)
                .eq("jobs.company_id", company.id)
                .eq("jobs.deleted", false)
                .eq("is_required", true)
                .or("quote_playground.is.null,quote_playground.eq.false")
                .not("expected_delivery_date", "is", null)
                .gte("expected_delivery_date", weekStart)
                .lt("expected_delivery_date", weekEnd)
                .limit(500);

            if (quoteError) throw quoteError;

            const included = (data || []).filter((quote) =>
                shouldIncludeInvoiceRecord(quote, company)
            );

            setRecords(included);
            setShouldShow(included.length > 0);
        } catch (err) {
            console.error("Weekly invoice popup error:", err);
            setError(err.message || "Could not load weekly invoice records.");
            setShouldShow(false);
        } finally {
            setChecking(false);
        }
    }, [company, companyTimeZone, loading, userProfile, weekStart, weekEnd]);

    const debouncedReload = useCallback(() => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

        refreshTimerRef.current = setTimeout(() => {
            loadPopupState();
        }, 700);
    }, [loadPopupState]);

    useEffect(() => {
        loadPopupState();

        return () => {
            if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        };
    }, [loadPopupState]);

    useEffect(() => {
        if (shouldShow) return;
        if (!userProfile?.id || !company?.id) return;

        const interval = setInterval(() => {
            loadPopupState();
        }, 1000 * 60 * 10); // every 2 minutes while hidden only

        return () => clearInterval(interval);
    }, [shouldShow, userProfile?.id, company?.id, loadPopupState]);

    useEffect(() => {
        if (!company?.id || !userProfile?.id || !hasPermission(userProfile)) return;

        const channel = supabase
            .channel(`weekly-invoice-records-${company.id}-${userProfile.id}`)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "job_quotes",
                },
                debouncedReload
            )
            .on(
                "postgres_changes",
                {
                    event: "INSERT",
                    schema: "public",
                    table: "weekly_invoice_confirmations",
                    filter: `user_id=eq.${userProfile.id}`,
                },
                debouncedReload
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [company?.id, userProfile, debouncedReload]);

    const handleOpenJob = useCallback((jobId) => {
        if (!jobId || !company?.slug) return;

        window.open(`/${company.slug}/assignment/${jobId}`, "_blank", "noopener,noreferrer");
    }, [company?.slug]);

    const handleEditJob = useCallback((jobId) => {
        if (!jobId) return;
        setEditingJob({ id: jobId });
    }, []);

    const handleConfirm = async () => {
        if (!userProfile?.id || !company?.id) return;

        setConfirming(true);
        setError("");

        try {
            const { error: insertError } = await supabase
                .from("weekly_invoice_confirmations")
                .upsert(
                    {
                        company_id: company.id,
                        user_id: userProfile.id,
                        week_start: weekStart,
                        confirmed_at: new Date().toISOString(),
                    },
                    {
                        onConflict: "company_id,user_id,week_start",
                    }
                );

            if (insertError) throw insertError;

            setShouldShow(false);
        } catch (err) {
            console.error("Weekly invoice confirm error:", err);
            setError(err.message || "Could not confirm weekly invoice records.");
        } finally {
            setConfirming(false);
        }
    };

    if (checking || !shouldShow) return null;

    return (
        <div className="mobile-dialog-shell fixed !mt-0 inset-0 z-[9999] bg-black/50 backdrop-blur-sm px-3 py-3 sm:px-6 sm:py-6">
            <div className="mx-auto flex h-full w-full max-w-7xl flex-col overflow-hidden rounded-[28px] border border-white/20 bg-white shadow-2xl">
                <div className="shrink-0 border-b border-gray-200 bg-gradient-to-b from-gray-50 to-white px-4 py-4 sm:px-6">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-start gap-3">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                                <FiAlertTriangle size={23} />
                            </div>

                            <div>
                                <h2 className="text-lg font-bold text-gray-950 sm:text-2xl">
                                    Weekly invoice review
                                </h2>
                                <p className="mt-1 text-sm text-gray-600">
                                    Confirm these selected invoice states for this week.
                                </p>
                                <p className="mt-1 text-xs text-gray-400">
                                    {weekStart} → {weekEnd} · {companyTimeZone}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col gap-2 lg:items-end">
                            <div className="flex flex-wrap items-center gap-2">
                                <QuoteTypePill
                                    type="all"
                                    count={records.length}
                                    active={activeFilter === "all"}
                                    disabled={records.length === 0}
                                    onClick={() => setActiveFilter("all")}
                                />

                                {QUOTE_ORDER.map((type) => (
                                    <QuoteTypePill
                                        key={type}
                                        type={type}
                                        count={groupedCounts[type] || 0}
                                        active={activeFilter === type}
                                        disabled={!groupedCounts[type]}
                                        onClick={() => setActiveFilter(type)}
                                    />
                                ))}
                            </div>

                            {activeFilter === "all" && (
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-[11px] font-bold text-gray-400 mr-1">
                                        Show groups:
                                    </span>

                                    {QUOTE_ORDER.map((type) => {
                                        const Icon = getQuoteTypeIcon(type);
                                        const disabled = !groupedCounts[type];

                                        return (
                                            <button
                                                key={type}
                                                type="button"
                                                disabled={disabled}
                                                onClick={() =>
                                                    setVisibleGroups((prev) => ({
                                                        ...prev,
                                                        [type]: !prev[type],
                                                    }))
                                                }
                                                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold transition ${disabled
                                                    ? "border-emerald-200 bg-emerald-50 text-emerald-300 cursor-not-allowed hidden"
                                                    : visibleGroups[type]
                                                        ? "border-gray-300 bg-gray-50 text-gray-700"
                                                        : "border-gray-200 bg-white text-gray-400"
                                                    }`}
                                            >
                                                <Icon size={12} />
                                                {QUOTE_LABELS[type]}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {error ? (
                    <div className="mx-4 mt-4 flex shrink-0 items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        <FiXCircle />
                        {error}
                    </div>
                ) : null}

                <div className="min-h-0 flex-1 overflow-auto">
                    <table className="w-full table-fixed border-collapse text-left">
                        <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgba(229,231,235,1)]">
                            <tr>
                                <th className="w-[130px] px-3 py-3 text-[11px] font-black uppercase tracking-wide text-gray-400">
                                    Job
                                </th>
                                <th className="px-3 py-3 text-[11px] font-black uppercase tracking-wide text-gray-400">
                                    Customer / Address
                                </th>
                                <th className="w-[145px] px-3 py-3 text-[11px] font-black uppercase tracking-wide text-gray-400">
                                    Delivery
                                </th>
                                <th className="w-[170px] px-3 py-3 text-[11px] font-black uppercase tracking-wide text-gray-400">
                                    Invoice
                                </th>
                                <th className="w-[155px] px-3 py-3 text-[11px] font-black uppercase tracking-wide text-gray-400">
                                    Actions
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {visibleRecords.map((quote) => (
                                <InvoiceRow
                                    key={quote.id}
                                    quote={quote}
                                    company={company}
                                    // onOpenJob={handleOpenJob}
                                    onEditJob={handleEditJob}
                                />
                            ))}
                        </tbody>
                    </table>
                    {visibleRecords.length === 0 && (
                        <div className="flex h-40 items-center justify-center text-sm font-semibold text-gray-400">
                            No records in this filter.
                        </div>
                    )}
                </div>

                <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-gray-500">
                            This popup will stay until confirmed.
                        </p>

                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={loadPopupState}
                                disabled={confirming}
                                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                            >
                                <FiRefreshCw />
                                Refresh
                            </button>

                            <button
                                type="button"
                                onClick={handleConfirm}
                                disabled={confirming}
                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-950 px-5 py-2 text-sm font-bold text-white hover:bg-black disabled:opacity-60"
                            >
                                <FiCheckCircle />
                                {confirming ? "Confirming..." : "Confirm records"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {editingJob && (
                <EditAssignment
                    modalJobId={editingJob.id}
                    modalCompanySlug={company?.slug}
                    onClose={(didUpdate) => {
                        setEditingJob(null);
                        if (didUpdate) {
                            loadPopupState();
                        }
                    }}
                />
            )}

        </div>
    );
};

export default WeeklyInvoiceRecordsPopup;